/**
 * Shared building blocks of the v2 detail page design language (Figma
 * "TrueTenant Phase 2", pre-screening frames): white radius-24 cards on a
 * warm off-white page, pastel icon squares, label-over-value field boxes.
 * Pastels anchor to Tailwind tokens (design hexes were eyeball estimates).
 */
import type { ReactNode } from 'react'
import { cn } from '#app/utils/misc'

/** Near-full-width content container (~75px gutters at 1920, design §1). */
export const DETAIL_CONTAINER = 'mx-auto w-full max-w-[1770px] px-4 sm:px-8'

/** Card shell — same soft shadow as application-form sections. */
export const DETAIL_CARD =
	'rounded-3xl bg-[#fbfdfd] shadow-[0px_0px_16px_0px_#0000000A,0px_0px_4px_0px_#0000000A]'

export type PastelTone =
	| 'green'
	| 'rose'
	| 'purple'
	| 'amber'
	| 'lamber'
	| 'red'
	| 'neutral'
	| 'dgreen'
	| 'damber'
	| 'mint'
	| 'blue'
	| 'sand'
	| 'blush'
	| 'lpurple'
	| 'lrose'
	| 'lred'

const SQUARE_TONES: Record<PastelTone, string> = {
	green: 'bg-[#D6E5BE40] text-[#A5BB83]',
	rose: 'bg-[#fbf5f8] text-[#BE7B97]',
	purple: 'bg-[#efeaf6] text-[#9976B8]',
	amber: 'bg-[#FFF6EA] text-[#F59F0A]',
	// Light amber — the Incomplete status chip's design pair (2026-07-13).
	lamber: 'bg-[#FFF4E5] text-[#F59F0A]',
	red: 'bg-[#FDF4F5] text-[#DC2634]',
	neutral: 'bg-gray-100 text-gray-600',
	dgreen: 'bg-[#F0FDF4] text-[#16A34A]',
	damber: 'bg-[#FFF6EA] text-[#F59F0A]',
	// Kebab-menu row pairs (client 2026-08-26): each action row carries its own
	// bg/icon pair rather than reusing the generic pastels.
	mint: 'bg-[#F3FAF6] text-[#16A34A]',
	blue: 'bg-[#E7F0F8] text-[#0669B3]',
	sand: 'bg-[#FEFAF2] text-[#F59F0A]',
	blush: 'bg-[#E7B3C940] text-[#E7B3C9]',
	lpurple: 'bg-[#DCCCEA66] text-[#9976B8]',
	lrose: 'bg-[#F6ABCB1A] text-[#BE7B97]',
	lred: 'bg-[#DC26341A] text-[#DC2634]',
}

export const CHIP_TONES: Record<PastelTone, string> = SQUARE_TONES

/** Pastel rounded icon square (~44-48px, design §1). */
export function IconSquare({
	tone,
	className,
	children,
}: {
	tone: PastelTone
	className?: string
	children: ReactNode
}) {
	return (
		<span
			className={cn(
				'flex size-12 shrink-0 items-center justify-center rounded-xl [&>svg]:size-5',
				SQUARE_TONES[tone],
				className,
			)}
		>
			{children}
		</span>
	)
}

/** Label-over-value field box (design §1/§8). Pass `children` for custom
 * value content (icons, suffixes); `value` falls back to '-' on gaps. */
export function FieldBox({
	label,
	value,
	fullWidth,
	className,
	children,
}: {
	label: string
	value?: string | null
	fullWidth?: boolean
	className?: string
	children?: ReactNode
}) {
	return (
		<div
			className={cn(
				'rounded-xl border border-[#F0F0F0] bg-[#fbfdfd] px-4 py-3',
				fullWidth && 'xl:col-span-2',
				className,
			)}
		>
			<span className="block text-[15px] text-[#747474]">{label}</span>
			{children ?? (
				// break-words so a long value with no spaces (e.g. an email) wraps
				// onto a second line instead of overflowing the box.
				<span className="block text-[15px] font-medium wrap-break-word text-[#32302F]">
					{value || '-'}
				</span>
			)}
		</div>
	)
}

/** Brand section glyph: a `public/img` SVG that already carries its own pastel
 * rounded tile, so it replaces {@link IconSquare} wholesale rather than sitting
 * inside one. Sized to the IconSquare (48px) so a swapped section header keeps
 * the same header rhythm as the lucide-glyph ones. */
export function SectionIcon({
	src,
	className,
}: {
	src: string
	className?: string
}) {
	return (
		<img
			src={src}
			alt=""
			aria-hidden="true"
			className={cn('size-12 shrink-0', className)}
		/>
	)
}

/** Card with pastel-icon-square header and a two-column field grid
 * (design §8 shared anatomy). Pass EITHER `tone` + `icon` (lucide glyph in a
 * pastel square) or `iconSrc` (a brand SVG that carries its own tile). */
export function SectionCard({
	tone,
	icon,
	iconSrc,
	title,
	className,
	headerRight,
	columns = 2,
	children,
}: {
	tone?: PastelTone
	icon?: ReactNode
	/** Brand SVG replacing the pastel square entirely; wins over tone/icon. */
	iconSrc?: string
	title: string
	className?: string
	headerRight?: ReactNode
	columns?: 1 | 2 | 3
	children: ReactNode
}) {
	return (
		<section className={cn(DETAIL_CARD, 'p-6 sm:p-7', className)}>
			<div className="flex items-center justify-between gap-3">
				<div className="flex items-center gap-3">
					{iconSrc ? (
						<SectionIcon src={iconSrc} />
					) : (
						<IconSquare tone={tone ?? 'neutral'}>{icon}</IconSquare>
					)}
					<h2 className="text-[16px] font-semibold text-[#32302F]">{title}</h2>
				</div>
				{headerRight}
			</div>
			<div
				className={cn(
					'mt-6 grid grid-cols-1 gap-4',
					columns === 3
						? 'md:grid-cols-3'
						: columns === 2
							? 'xl:grid-cols-2'
							: null,
				)}
			>
				{children}
			</div>
		</section>
	)
}

/** Fully-rounded pastel chip (header-card chips, switcher status chips). */
export function PastelChip({
	tone,
	icon,
	label,
	className,
}: {
	tone: PastelTone
	icon?: ReactNode
	label: string
	className?: string
}) {
	return (
		<span
			className={cn(
				'inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium [&>svg]:size-4',
				CHIP_TONES[tone],
				className,
			)}
		>
			{icon}
			{label}
		</span>
	)
}
